/**
 * DraftWhatsAppLink — small reusable WhatsApp affordance for draft order cards/rows.
 *
 * Matches the ActionCluster WhatsApp pattern exactly:
 *  - href: https://wa.me/<digits> (strip non-digits from phoneE164)
 *  - color: bg-[color:var(--wa-green)] / text-[color:var(--wa-green)]
 *  - icon: same SVG path used in ActionCluster
 *  - aria-label: keys.orders.rowWhatsappAriaLabel (same key as ActionCluster)
 *
 * Used in IncompleteDraftRow (desktop row + mobile draft card) and
 * OrdersGridCard (draft card footer).
 */

import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";

type Variant =
  /** Icon-only square button (desktop row / card footer) */
  | "icon"
  /** Labelled pill button (mobile draft card footer) */
  | "pill";

type Props = {
  phoneE164: string;
  customerName: string;
  variant?: Variant;
  /** Extra CSS classes appended to the root element */
  className?: string;
};

function toWaDigits(phoneE164: string): string {
  return phoneE164.replace(/[^\d]/g, "");
}

/** WhatsApp SVG glyph — identical to ActionCluster's inline SVG */
function WaGlyph({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

export function DraftWhatsAppLink({
  phoneE164,
  customerName,
  variant = "icon",
  className = "",
}: Props) {
  const t = useTranslations();
  const digits = toWaDigits(phoneE164);

  if (!digits) return null;

  const ariaLabel = t(keys.orders.rowWhatsappAriaLabel, { name: customerName });

  if (variant === "pill") {
    return (
      <a
        href={`https://wa.me/${digits}`}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        aria-label={ariaLabel}
        className={[
          "inline-flex flex-1 items-center justify-center gap-2 rounded-[13px] h-12 px-5",
          "bg-[color:var(--wa-green)] text-white text-[14.5px] font-bold transition-opacity hover:opacity-90",
          className,
        ].join(" ")}
      >
        <WaGlyph className="h-[22px] w-[22px]" />
        {t(keys.orders.whatsappAction)}
      </a>
    );
  }

  // variant === "icon": square icon-only button (matches ActionCluster md size)
  return (
    <a
      href={`https://wa.me/${digits}`}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      aria-label={ariaLabel}
      className={[
        "inline-flex shrink-0 h-[42px] w-[42px] rounded-[11px]",
        "items-center justify-center bg-[color:var(--wa-green)] text-white transition-opacity hover:opacity-90",
        className,
      ].join(" ")}
    >
      <WaGlyph className="h-[19px] w-[19px]" />
    </a>
  );
}
